import { mountPassFill } from "@act/client-core/passFill";
import type { PassAwardMessage } from "@act/shared";
import type { UiNav } from "../input/GamepadInput";

export interface ResultLine {
  label: string;
  value: string;
  highlight?: boolean;
}

/**
 * Post-race results overlay: a title, a few stat lines, the battle pass's XP fill when the
 * race's award comes back (ACT HQ t-1104: Theo, 14 Oct 2026, "lets add battlepass progress
 * to the post game screens of all the games"), and Retry / Menu. A pad moves between the two
 * buttons, A presses one (or skips a fill still playing) and B is Menu.
 */
export class Results {
  private root: HTMLDivElement;
  /** The fill's place. It outlives re-renders (online results redraw as the room changes). */
  private readonly passHost = document.createElement("div");
  private fill: ReturnType<typeof mountPassFill> | null = null;
  private filling = false;
  /** Bumped each time the screen goes away: an award for an earlier showing is dropped. */
  private showing = 0;
  private focus = 0;
  /** A level reached on the fill (the game plays a sound). */
  onLevelUp: () => void = () => {};

  constructor(container: HTMLElement) {
    this.root = document.createElement("div");
    this.root.className = "results overlay";
    this.root.style.display = "none";
    this.passHost.className = "results-pass";
    container.appendChild(this.root);
  }

  get visible(): boolean {
    return this.root.style.display !== "none";
  }

  /** Show (or redraw) the results. Returns this showing, for showPass. */
  show(
    title: string,
    subtitle: string,
    lines: ResultLine[],
    onRetry: () => void,
    onMenu: () => void
  ): number {
    const rows = lines
      .map(
        (l) => `
        <div class="result-row ${l.highlight ? "hot" : ""}">
          <span class="result-label">${l.label}</span>
          <span class="result-value">${l.value}</span>
        </div>`
      )
      .join("");

    this.root.innerHTML = `
      <div class="results-inner">
        <h1 class="results-title">${title}</h1>
        ${subtitle ? `<p class="results-sub">${subtitle}</p>` : ""}
        <div class="result-rows">${rows}</div>
        <div class="results-pass-slot"></div>
        <div class="results-btns">
          <button class="start-btn retry-btn">RETRY</button>
          <button class="back-btn menu-btn">MENU</button>
        </div>
      </div>`;

    this.root.querySelector(".results-pass-slot")!.replaceWith(this.passHost);
    this.root.querySelector<HTMLButtonElement>(".retry-btn")!.addEventListener("click", onRetry);
    this.root.querySelector<HTMLButtonElement>(".menu-btn")!.addEventListener("click", onMenu);
    if (!this.visible) this.focus = 0;
    this.renderFocus();
    this.root.style.display = "";
    return this.showing;
  }

  /** Play the race's pass award (once per showing; dropped if the screen has moved on). */
  showPass(award: PassAwardMessage, showing: number): void {
    if (showing !== this.showing || !this.visible || this.fill) return;
    this.filling = true;
    const fill = mountPassFill(this.passHost, award, { onLevelUp: () => this.onLevelUp() });
    this.fill = fill;
    void fill.done.then(() => {
      if (this.fill === fill) this.filling = false;
    });
  }

  handlePad(nav: UiNav): void {
    if (nav.confirm && this.filling) {
      this.fill?.skip();
      return;
    }
    if (nav.left || nav.right || nav.up || nav.down) {
      this.focus = 1 - this.focus;
      this.renderFocus();
    }
    if (nav.confirm) this.buttons()[this.focus]?.click();
    else if (nav.back) this.buttons()[1]?.click();
  }

  hide(): void {
    this.fill?.dispose();
    this.fill = null;
    this.filling = false;
    this.showing += 1;
    this.root.style.display = "none";
  }

  private buttons(): HTMLButtonElement[] {
    return Array.from(this.root.querySelectorAll<HTMLButtonElement>(".results-btns button"));
  }

  private renderFocus(): void {
    this.buttons().forEach((b, i) => b.classList.toggle("focus", i === this.focus));
  }
}
